import { useState, useEffect, useRef } from 'react';
import { FaTimes } from 'react-icons/fa';
import { GrNext } from 'react-icons/gr';
import { useSprings, animated } from 'react-spring'
import { useDrag } from '@use-gesture/react'
import { clamp } from 'lodash'

import '../css/lightBox.css'
import { AnimatePresence, motion } from 'framer-motion';
import { wrap } from 'popmotion';


export interface GalleryLightBoxProps {
   isOpen: boolean;
   imagePage: number;
   images: string[];
   mediaLength: number;
   onClose: () => void;
}

const GalleryLightBox = ({ isOpen, images, onClose, imagePage, mediaLength }: GalleryLightBoxProps) => {
   const [[page, direction], setPage] = useState([imagePage, 0]);
   const [index, setIndex] = useState<number>(1);

   useEffect(() => {
      document.addEventListener('keyup', (e) => {
         e.code === "Escape" && onClose()
      });
   }, [])

   // We only have 3 images, but we paginate them absolutely (ie 1, 2, 3, 4, 5...) and
   // then wrap that within 0-2 to find our image ID in the array below. By passing an
   // absolute page index as the `motion` component's `key` prop, `AnimatePresence` will
   // detect it as an entirely new image. So you can infinitely paginate as few as 1 images.
   const imageIndex = wrap(0, images.length, page);

   const paginate = (newDirection: number) => {
      setPage([page + newDirection, newDirection]);
      if (index === mediaLength && newDirection > 0)
         setIndex(1);
      else if (index === 1 && newDirection < 0)
         setIndex(mediaLength)
      else if (newDirection > 0)
         setIndex(pre => pre + 1)
      else if (newDirection < 0)
         setIndex(pre => pre - 1)
   };
   const [currentImage, setCurrentImage] = useState<string>("");

   const variants = {
      enter: (direction: number) => {
         return {
            x: direction > 0 ? 1000 : -1000,
            opacity: 0
         };
      },
      center: {
         zIndex: 1,
         x: 0,
         opacity: 1
      },
      exit: (direction: number) => {
         return {
            zIndex: 0,
            x: direction < 0 ? 1000 : -1000,
            opacity: 0
         };
      }
   };

   const swipeConfidenceThreshold = 10000;
   const swipePower = (offset: number, velocity: number) => {
      return Math.abs(offset) * velocity;
   };

   useEffect(() => {
      setCurrentImage(images[0]);
   }, [])


   const showImage = (image: string) => {
      setCurrentImage(image);
   };


   return (
      <>
         <div id="lightbox">
            <div className="lightbox_nav">
               <div className="nav_buttons">
                  <div className="lightbox-flex-centered lightbox_nav_button" onClick={onClose}>
                     <FaTimes />
                  </div>
               </div>
               <div style={{ position: 'absolute', top: '10px', left: '5px', color: 'white', backgroundColor: 'rgba(255,255,255,0.3)', padding: '0 5px', borderRadius: '15px', fontSize: '0.9rem', fontWeight: 'normal' }}>
                  {mediaLength} \ {index}
               </div>
            </div>
            <div className="wrapper">
               <AnimatePresence initial={false} custom={direction}>
                  <motion.img
                     className={"lightbox_image"}
                     key={page}
                     src={images[imageIndex]}
                     custom={direction}
                     variants={variants}
                     initial="enter"
                     animate="center"
                     exit="exit"
                     transition={{
                        x: { type: "spring", stiffness: 300, damping: 30 },
                        opacity: { duration: 0.2 }
                     }}
                     drag="x"
                     dragConstraints={{ left: 0, right: 0 }}
                     dragElastic={1}
                     onDragEnd={(e, { offset, velocity }) => {
                        const swipe = swipePower(offset.x, velocity.x);

                        if (swipe < -swipeConfidenceThreshold) {
                           paginate(1);
                        } else if (swipe > swipeConfidenceThreshold) {
                           paginate(-1);
                        }
                     }}
                  />
               </AnimatePresence>
            </div>
            <div className="next" onClick={() => paginate(1)} style={{ backgroundColor: 'rgba(255,255,255,0.5)' }}>
               <GrNext />
            </div>
            <div className="prev" onClick={() => paginate(-1)} style={{ backgroundColor: 'rgba(255,255,255,0.5)' }}>
               <GrNext />
            </div>
         </div>
      </>
   )

}

export default GalleryLightBox;


